import {
  AcceptShareResponse,
  AnswerPageResponse,
  BootstrapResponse,
  CreateShareResponse,
  LevelAdjustmentRequest,
  ProfileViewResponse,
  PublicShareInvitation,
  ShareListResponse,
  SharePermission,
  StudentCreateRequest,
  StudentDetailResponse,
  StudentPatchRequest,
  SyncBatchRequest,
  SyncBatchResponse,
} from "../shared/api.js";
import { AuthTokenError, getAccessToken } from "./auth0.js";

export const needsSignIn = (error: unknown): boolean =>
  error instanceof AuthTokenError && error.problem === "reauthenticate";

const API_BASE_URL = (process.env.READING_BEE_API_BASE_URL ?? "/.netlify/functions/api").replace(/\/$/, "");

export class ReadingBeeApiError extends Error {
  readonly status: number;
  readonly code: string | null;

  constructor(status: number, message: string, code: string | null = null) {
    super(message);
    this.name = "ReadingBeeApiError";
    this.status = status;
    this.code = code;
  }
}

const parseError = async (response: Response): Promise<ReadingBeeApiError> => {
  try {
    const body = (await response.json()) as { error?: { code?: unknown; message?: unknown } };
    const message =
      typeof body.error?.message === "string" ? body.error.message : `Request failed (${response.status})`;
    const code = typeof body.error?.code === "string" ? body.error.code : null;
    return new ReadingBeeApiError(response.status, message, code);
  } catch {
    return new ReadingBeeApiError(response.status, `Request failed (${response.status})`);
  }
};

export class ReadingBeeApi {
  constructor(
    private readonly token = getAccessToken,
    private readonly request = globalThis.fetch.bind(globalThis),
  ) {}

  async bootstrap(): Promise<BootstrapResponse> {
    return this.get("/v1/me/bootstrap", BootstrapResponse);
  }

  async syncBatch(batch: SyncBatchRequest): Promise<SyncBatchResponse> {
    return this.post("/v1/sync/batch", batch, SyncBatchResponse);
  }

  async createStudent(input: StudentCreateRequest): Promise<unknown> {
    return this.post("/v1/students", input);
  }

  async patchStudent(studentId: string, input: StudentPatchRequest): Promise<unknown> {
    return this.patch(`/v1/students/${encodeURIComponent(studentId)}`, input);
  }

  async student(studentId: string): Promise<StudentDetailResponse> {
    return this.get(`/v1/students/${encodeURIComponent(studentId)}`, StudentDetailResponse);
  }

  async profileView(studentId: string): Promise<ProfileViewResponse> {
    return this.get(`/v1/students/${encodeURIComponent(studentId)}/profile-view`, ProfileViewResponse);
  }

  async answers(studentId: string, cursor?: string): Promise<AnswerPageResponse> {
    const query = cursor ? `?cursor=${encodeURIComponent(cursor)}` : "";
    return this.get(`/v1/students/${encodeURIComponent(studentId)}/answers${query}`, AnswerPageResponse);
  }

  async adjustLevel(studentId: string, input: LevelAdjustmentRequest): Promise<unknown> {
    return this.post(`/v1/students/${encodeURIComponent(studentId)}/level-adjustments`, input);
  }

  async createShare(
    studentId: string,
    input: { email: string; permission: SharePermission },
  ): Promise<CreateShareResponse> {
    return this.post(`/v1/students/${encodeURIComponent(studentId)}/shares`, input, CreateShareResponse);
  }

  async listShares(): Promise<ShareListResponse> {
    return this.get("/v1/me/shares", ShareListResponse);
  }

  async getShareInvitation(token: string): Promise<PublicShareInvitation> {
    return this.publicGet(`/v1/share-invitations/${encodeURIComponent(token)}`, PublicShareInvitation);
  }

  async acceptShare(token: string): Promise<AcceptShareResponse> {
    return this.post(`/v1/share-invitations/${encodeURIComponent(token)}/accept`, {}, AcceptShareResponse);
  }

  async revokeShare(studentId: string, shareId: string): Promise<void> {
    await this.send(`/v1/students/${encodeURIComponent(studentId)}/shares/${encodeURIComponent(shareId)}`, {
      method: "DELETE",
    });
  }

  async updateSharePermission(studentId: string, shareId: string, permission: SharePermission): Promise<void> {
    await this.patch(`/v1/students/${encodeURIComponent(studentId)}/shares/${encodeURIComponent(shareId)}`, {
      permission,
    });
  }

  async leaveShare(shareId: string): Promise<void> {
    await this.send(`/v1/me/shares/${encodeURIComponent(shareId)}`, { method: "DELETE" });
  }

  private async get<T>(path: string, schema: { parse: (value: unknown) => T }): Promise<T> {
    return this.send(path, { method: "GET" }, schema);
  }

  private async publicGet<T>(path: string, schema: { parse: (value: unknown) => T }): Promise<T> {
    return this.send(path, { method: "GET" }, schema, false);
  }

  private async post<T>(path: string, body: unknown, schema?: { parse: (value: unknown) => T }): Promise<T> {
    return this.send(
      path,
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(body),
      },
      schema,
    );
  }

  private async patch<T>(path: string, body: unknown, schema?: { parse: (value: unknown) => T }): Promise<T> {
    return this.send(
      path,
      {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(body),
      },
      schema,
    );
  }

  private async send<T>(
    path: string,
    init: RequestInit,
    schema?: { parse: (value: unknown) => T },
    authenticated = true,
  ): Promise<T> {
    const headers = new Headers(init.headers);
    if (authenticated) {
      const accessToken = await this.token();
      if (!accessToken) {
        throw new AuthTokenError("reauthenticate", "Please sign in to sync Reading Bee profiles.");
      }
      headers.set("authorization", `Bearer ${accessToken}`);
    }
    const response = await this.request(`${API_BASE_URL}${path}`, { ...init, headers });
    if (response.status === 401 && authenticated) {
      throw new AuthTokenError(
        "reauthenticate",
        "Your instructor sign-in is no longer valid. Please sign in again to keep syncing profiles.",
        { cause: await parseError(response) },
      );
    }
    if (!response.ok) throw await parseError(response);
    if (!schema) return undefined as T;
    return schema.parse(await response.json());
  }
}

export const readingBeeApi = new ReadingBeeApi();
